import { FirebaseDatabaseService } from '../services/firebaseDatabaseService';

interface BaseGameResult {
  sessionId: string;
  timestamp: string;
  userId?: string;
}

interface NumberSequenceResultData extends BaseGameResult {
  level: number;
  timestampStart: string;
  timestampEnd: string;
  gridSize: number;
  obstaclesCount: number;
  movesCount: number;
  errorsCount: number;
  completionTimeSeconds: number;
  accuracyPercentage: number;
  score: number;
  difficultyIndex: number;
  ddaAction: string;
  gameData?: string;
}

interface MemoryPalaceResultData extends BaseGameResult {
  accuracyPercentage: number;
  timeSeconds: number;
  score: number;
  difficultyLevel: number;
  objectsCount: number;
  gridSize: number;
  memorizationTimeSeconds: number;
  correctPlacements: number;
  totalPlacements: number;
  objectPositions?: string;
  playerPlacements?: string;
}

interface ToneMemoryResultData extends BaseGameResult {
  round: number;
  sequenceLength: number;
  accuracyPercentage: number;
  score: number;
  reactionTimeMs: number;
  difficultyLevel: number;
  isCompleted: boolean;
  originalSequence?: string;
  playerSequence?: string;
  timingData?: string;
}

interface ImageMatchingResultData extends BaseGameResult {
  round: number;
  gridSize: number;
  pairsCount: number;
  movesCount: number;
  matchesFound: number;
  timeSeconds: number;
  accuracyPercentage: number;
  score: number;
  difficultyLevel: number;
  isCompleted: boolean;
  cardLayout?: string;
  playerMoves?: string;
  timingData?: string;
}

interface TickerTapeResultData extends BaseGameResult {
  round: number;
  stocksCount: number;
  correctAnswers: number;
  totalAnswers: number;
  accuracyPercentage: number;
  scrollSpeedPixelsPerSecond: number;
  displayTimeSeconds: number;
  reactionTimeSeconds: number;
  difficultyLevel: number;
  isGameCompleted: boolean;
  overallAccuracy: number;
  stockData?: string;
  playerAnswers?: string;
  ddaData?: string;
}

/**
 * Utility class for saving game results to Firebase Database only
 */
export class GameResultSaver {
  
  static async saveNumberSequenceResult(data: NumberSequenceResultData): Promise<void> {
    try {
      console.log('Saving NumberSequence result to Firebase:', data);
      await FirebaseDatabaseService.saveNumberSequenceResult(data);
      console.log('NumberSequence result saved successfully to Firebase');
    } catch (error) {
      console.error('Failed to save NumberSequence result to Firebase:', error);
      throw error;
    }
  }

  static async saveMemoryPalaceResult(data: MemoryPalaceResultData): Promise<void> {
    try {
      console.log('Saving MemoryPalace result to Firebase:', data);
      await FirebaseDatabaseService.saveMemoryPalaceResult(data);
      console.log('MemoryPalace result saved successfully to Firebase');
    } catch (error) {
      console.error('Failed to save MemoryPalace result to Firebase:', error);
      throw error;
    }
  }

  static async saveToneMemoryResult(data: ToneMemoryResultData): Promise<void> {
    try {
      console.log('Saving ToneMemory result to Firebase:', data);
      await FirebaseDatabaseService.saveToneMemoryResult(data);
      console.log('ToneMemory result saved successfully to Firebase');
    } catch (error) {
      console.error('Failed to save ToneMemory result to Firebase:', error);
      throw error;
    }
  }

  static async saveImageMatchingResult(data: ImageMatchingResultData): Promise<void> {
    try {
      console.log('Saving ImageMatching result to Firebase:', data);
      await FirebaseDatabaseService.saveImageMatchingResult(data);
      console.log('ImageMatching result saved successfully to Firebase');
    } catch (error) {
      console.error('Failed to save ImageMatching result to Firebase:', error);
      throw error;
    }
  }

  static async saveTickerTapeResult(data: TickerTapeResultData): Promise<void> {
    try {
      console.log('Saving TickerTape result to Firebase:', data);
      await FirebaseDatabaseService.saveTickerTapeResult(data);
      console.log('TickerTape result saved successfully to Firebase');
    } catch (error) {
      console.error('Failed to save TickerTape result to Firebase:', error);
      throw error;
    }
  }

  // Generic method to save any game result
  static async saveGameResult(gameType: string, data: any): Promise<void> {
    try {
      console.log(`Saving ${gameType} result to Firebase:`, data);
      
      switch (gameType.toLowerCase()) {
        case 'numbersequence':
          await this.saveNumberSequenceResult(data);
          break;
        case 'memorypalace':
          await this.saveMemoryPalaceResult(data);
          break;
        case 'tonememory':
          await this.saveToneMemoryResult(data);
          break;
        case 'imagematching':
          await this.saveImageMatchingResult(data);
          break;
        case 'tickertape':
          await this.saveTickerTapeResult(data);
          break;
        default:
          throw new Error(`Unknown game type: ${gameType}`);
      }
      
      console.log(`${gameType} result saved successfully to Firebase`);
    } catch (error) {
      console.error(`Failed to save ${gameType} result to Firebase:`, error);
      throw error;
    }
  }

  // Save patient session data
  static async savePatientSession(sessionData: any): Promise<string> {
    try {
      console.log('Saving patient session to Firebase:', sessionData);
      const sessionId = await FirebaseDatabaseService.createGameSession(
        sessionData.userId || sessionData.patientId,
        sessionData.gameType || 'general'
      );
      console.log('Patient session saved to Firebase with ID:', sessionId);
      return sessionId;
    } catch (error) {
      console.error('Failed to save patient session to Firebase:', error);
      throw error;
    }
  }

  // Batch save multiple game results
  static async batchSaveResults(results: Array<{gameType: string, data: any}>): Promise<void> {
    try {
      console.log('Batch saving results to Firebase:', results);
      const promises = results.map(result => 
        this.saveGameResult(result.gameType, result.data)
      );
      await Promise.all(promises);
      console.log('Batch save completed successfully');
    } catch (error) {
      console.error('Error in batch save:', error);
      throw error;
    }
  }

  /**
   * Generic save method that handles errors gracefully
   */
  static async saveResultSafely<T>(
    saveFunction: () => Promise<void>,
    gameType: string,
    data: T
  ): Promise<void> {
    try {
      await saveFunction();
    } catch (error) {
      console.error(`Failed to save ${gameType} result to Firebase:`, error, 'Data:', data);
      // Don't throw error - game should continue even if database save fails
    }
  }
}

export type {
  NumberSequenceResultData,
  MemoryPalaceResultData,
  ToneMemoryResultData,
  ImageMatchingResultData,
  TickerTapeResultData
};